import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import BranchSelect from "../../components/ui/BranchSelect";
import {
  useLeaveCount,
  useLeaveRequests,
  useLeaveTypes,
} from "../../api/queries/leaveQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime, fromNow } from "../../utils/dateTime";
import { employeeName } from "../../utils/text";
import LeaveStatusBadge from "./components/review/LeaveStatusBadge";
import LeaveRequestDrawer from "./components/review/LeaveRequestDrawer";
import { useLeaveReviewDialogs } from "./components/review/LeaveReviewDialogs";
import { LevelBadge } from "../approvals/components/ApprovalTrail";
import { useCanModify } from "../../hooks/usePermissions";
import {
  formatLeaveDates,
  isAwaitingDecision,
} from "./components/review/leaveFormat";
import {
  LeaveChangeSummary,
  LeaveChangeTag,
} from "./components/review/LeaveChangeDetails";
import "./leave.css";

const TABS = [
  { key: "pending", label: "Pending", icon: "bi-hourglass-split" },
  { key: "approved", label: "Approved", icon: "bi-check-circle" },
  { key: "rejected", label: "Rejected", icon: "bi-x-circle" },
  { key: "cancelled", label: "Cancelled", icon: "bi-slash-circle" },
  { key: "all", label: "All", icon: "bi-list-ul" },
];
const FILTER_KEYS = [
  "leaveTypeId",
  "branchId",
  "from",
  "to",
  "search",
  "myTurn",
];

const EMPTY_TEXT = {
  pending: "No leave requests waiting for your review.",
  approved: "No approved leave.",
  rejected: "No rejected leave.",
  cancelled: "No cancelled leave.",
  all: "No leave requests.",
};

/** Count badge for one tab (limit=1, read `total`). */
const TabCount = ({ status, active }) => {
  const { data } = useLeaveCount(status);
  if (data === undefined) return null;
  return (
    <span
      className={`approval-tab-count ${active && status !== "pending" ? "approval-tab-count--plain" : ""}`}
      aria-label={`${data} requests`}
    >
      {data}
    </span>
  );
};

const LeaveApprovalsPage = () => {
  // View state lives in the URL so refresh / Back keep the same list.
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("status"))
    ? params.get("status")
    : "pending";
  const page = Number(params.get("page")) || 1;
  const limit = Number(params.get("limit")) || 20;
  const openId = params.get("request");
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])),
    [params],
  );
  const hasFilters = FILTER_KEYS.some((k) => filters[k]);

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k),
    );
    if (resetPage && !("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  // Search box: type freely, the URL (and the request) follows after 300ms
  const [searchText, setSearchText] = useState(filters.search);
  useEffect(() => {
    const value = searchText.trim();
    if (value === filters.search) return undefined;
    const t = setTimeout(() => update({ search: value }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const typesQuery = useLeaveTypes();
  const listQuery = useLeaveRequests({
    ...filters,
    status: tab === "all" ? undefined : tab,
    page,
    limit,
  });
  const rows = listQuery.data?.data || [];
  const total = listQuery.data?.total || 0;

  const { open, isOpen, dialog, isForbidden } = useLeaveReviewDialogs();
  // Configured roles need Leave: modify to approve / reject / cancel / file (the server's canReview still applies)
  const canModify = useCanModify("leave");
  const openDrawer = (id) => update({ request: id }, { resetPage: false });
  const closeDrawer = () => update({ request: "" }, { resetPage: false });

  const clearFilters = () => {
    setSearchText("");
    update(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])));
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">
              Leave Approvals
            </h1>
            <p className="text-muted small mb-0">
              Review leave requests from employees in your scope.
            </p>
          </div>
          {canModify && (
            <Link
              to="/leave/file"
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
            >
              <i className="bi bi-plus-lg me-2"></i> File leave for employee
            </Link>
          )}
        </div>

        <div className="approval-tabs" role="tablist" aria-label="Leave status">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`approval-tab ${tab === t.key ? "active" : ""}`}
              onClick={() =>
                update({ status: t.key === "pending" ? "" : t.key })
              }
            >
              <i className={`bi ${t.icon}`}></i> {t.label}
              <TabCount status={t.key} active={tab === t.key} />
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="leave-filters mb-3">
          <div className="position-relative leave-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search name or employee number"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search name or employee number"
            />
          </div>
          <select
            className="status-select"
            value={filters.leaveTypeId}
            onChange={(e) => update({ leaveTypeId: e.target.value })}
            aria-label="Leave type"
          >
            <option value="">All leave types</option>
            {(typesQuery.data || []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <div className="leave-filter-branch">
            <BranchSelect
              value={filters.branchId}
              onChange={(v) => update({ branchId: v })}
              id="leave-branch"
            />
          </div>
          <div className="leave-filter-dates">
            <input
              type="date"
              className="filter-input"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => update({ from: e.target.value })}
              aria-label="From date"
            />
            <span className="text-slate-400">–</span>
            <input
              type="date"
              className="filter-input"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => update({ to: e.target.value })}
              aria-label="To date"
            />
          </div>
          {tab === "pending" && (
            <div className="form-check form-switch m-0">
              <input
                id="leave-my-turn"
                type="checkbox"
                className="form-check-input"
                checked={filters.myTurn === "true"}
                onChange={(e) =>
                  update({ myTurn: e.target.checked ? "true" : "" })
                }
              />
              <label
                htmlFor="leave-my-turn"
                className="form-check-label fs-080"
              >
                My turn only
              </label>
            </div>
          )}
          {hasFilters && (
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
          {listQuery.isFetching && !listQuery.isLoading && (
            <span
              className="spinner-border spinner-border-sm text-primary ms-auto"
              role="status"
              aria-label="Refreshing"
            ></span>
          )}
        </div>

        <div className="glass-table-card">
          {listQuery.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(
                listQuery.error,
                "Could not load leave requests.",
              )}
              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => listQuery.refetch()}
                >
                  Try again
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0 leave-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th className="ps-4 py-3">Employee</th>
                      <th className="py-3">Branch</th>
                      <th className="py-3">Department</th>
                      <th className="py-3">Type</th>
                      <th className="py-3">Dates</th>
                      <th className="py-3 text-end">Days</th>
                      <th className="py-3">Reason</th>
                      <th className="py-3">Filed</th>
                      <th className="py-3">Status</th>
                      <th className="py-3 pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {rows.map((r) => {
                      // Not my level yet (multi-level) or refused earlier
                      const blocked =
                        isForbidden(r.id) || r.canReview === false;
                      return (
                        <tr
                          key={r.id}
                          className="cursor-pointer"
                          onClick={() => openDrawer(r.id)}
                        >
                          <td className="ps-4">
                            <div className="emp-name-text">
                              {employeeName(r.employee)}
                            </div>
                            <div className="emp-sub-text">
                              {r.employee?.employeeId}
                            </div>
                          </td>
                          <td>{r.employee?.branch?.branchName || "—"}</td>
                          <td>
                            {r.employee?.department?.departmentName || "—"}
                          </td>
                          <td className="fw-semibold">
                            {r.leaveType?.name || "—"}
                          </td>
                          <td className="text-nowrap">
                            {formatLeaveDates(r.startDate, r.endDate)}
                          </td>
                          <td className="text-end tabular-nums">
                            {Number(r.totalDays) || 0}
                          </td>
                          <td>
                            <div className="approval-reason" title={r.reason}>
                              {r.reason || "—"}
                            </div>
                          </td>
                          <td
                            className="text-nowrap"
                            title={formatDateTime(r.createdAt)}
                          >
                            {fromNow(r.createdAt)}
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              <LeaveStatusBadge status={r.status} />
                              <LeaveChangeTag request={r} />
                            </div>
                            <LeaveChangeSummary request={r} />
                            {r.status === "pending" && (
                              <div className="mt-1">
                                <LevelBadge
                                  levels={r.levels}
                                  status="PENDING"
                                />
                              </div>
                            )}
                          </td>
                          <td
                            className="pe-4 text-end text-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isAwaitingDecision(r) && canModify && (
                              <div className="d-inline-flex gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-success leave-action-btn"
                                  onClick={() => open("approve", r)}
                                  disabled={blocked}
                                  title={
                                    blocked
                                      ? r.levels?.total > 1 &&
                                        r.canReview === false
                                        ? `Waiting for the level ${r.levels.current} approver`
                                        : "You can't review this request"
                                      : undefined
                                  }
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger leave-action-btn"
                                  onClick={() => open("reject", r)}
                                  disabled={blocked}
                                  title={
                                    blocked
                                      ? r.levels?.total > 1 &&
                                        r.canReview === false
                                        ? `Waiting for the level ${r.levels.current} approver`
                                        : "You can't review this request"
                                      : undefined
                                  }
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                            {r.status === "approved" &&
                              !r.pendingChange &&
                              canModify && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-warning leave-action-btn"
                                  onClick={() => open("cancel", r)}
                                  disabled={blocked}
                                >
                                  Cancel
                                </button>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                    {!listQuery.isLoading && rows.length === 0 && (
                      <tr>
                        <td colSpan={10} className="approval-empty">
                          <i className="bi bi-inbox"></i>
                          {hasFilters
                            ? "Nothing matches these filters."
                            : EMPTY_TEXT[tab]}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {listQuery.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <Pagination
                page={page}
                limit={limit}
                total={total}
                onPageChange={(p) =>
                  update({ page: String(p) }, { resetPage: false })
                }
                onLimitChange={(l) => update({ limit: String(l) })}
              />
            </>
          )}
        </div>

        {tab === "pending" && total > 0 && (
          <p className="text-slate-500 fs-075 mt-2 mb-0">
            Oldest requests are shown first.
          </p>
        )}
      </div>

      {openId && (
        <LeaveRequestDrawer
          id={openId}
          onClose={closeDrawer}
          onAction={open}
          isForbidden={isForbidden}
          paused={isOpen}
          readOnly={!canModify}
        />
      )}
      {dialog}
    </Layout>
  );
};

export default LeaveApprovalsPage;
